import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import {
    fetchOrgAdmins,
    issueOrgAdmin,
    OrgAdminApiError,
    type OrgAdminIssued,
} from '@/api/orgAdmin';
import { EmptyState, ErrorState, Modal, SectionCard, SkeletonTable, useToast } from '@/components/ui';

interface OrgAdminsTabProps {
    orgId: string;
    /** Issuing a NEW org-admin account is super-admin only (D-09,
     * `OrganizationAdminsView.post`) — org-admins may view this list for
     * their own org but never grant a peer. */
    isSuperAdmin: boolean;
}

/**
 * EAA-1815: `/organizations/:id?tab=admins` — full quản trị viên management.
 * Logic unchanged from the pre-redesign detail page (temp-password shown
 * exactly once, D12).
 */
export function OrgAdminsTab({ orgId, isSuperAdmin }: OrgAdminsTabProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();

    const [adminForm, setAdminForm] = useState({ email: '', username: '' });
    const [adminModalOpen, setAdminModalOpen] = useState(false);
    const [adminFormError, setAdminFormError] = useState<string | null>(null);
    const [issued, setIssued] = useState<OrgAdminIssued | null>(null);

    const adminsQuery = useQuery({
        queryKey: ['organization-admins', orgId, accessToken],
        queryFn: () => fetchOrgAdmins(config.apiBase, accessToken as string, orgId),
        retry: false,
    });
    const admins = adminsQuery.data ?? [];

    const issueAdminMutation = useMutation({
        mutationFn: () =>
            issueOrgAdmin(config.apiBase, accessToken as string, orgId, {
                email: adminForm.email.trim(),
                username: adminForm.username.trim(),
            }),
        onSuccess: (data) => {
            setIssued(data);
            setAdminForm({ email: '', username: '' });
            setAdminFormError(null);
            setAdminModalOpen(false);
            toast.success(t('admin.organizations.adminsTab.toastIssued', { username: data.username }));
            void queryClient.invalidateQueries({ queryKey: ['organization-admins', orgId] });
        },
        onError: (error) => {
            setAdminFormError(
                error instanceof OrgAdminApiError
                    ? error.message
                    : t('admin.organizations.adminsTab.genericIssueError')
            );
        },
    });

    const onIssueAdmin = (event: FormEvent) => {
        event.preventDefault();
        if (!adminForm.email.trim() || !adminForm.username.trim()) {
            setAdminFormError(t('admin.organizations.adminsTab.form.validationError'));
            return;
        }
        issueAdminMutation.mutate();
    };

    const openAdminModal = () => {
        setAdminFormError(null);
        setAdminModalOpen(true);
    };
    const closeAdminModal = () => {
        if (issueAdminMutation.isPending) return;
        setAdminModalOpen(false);
    };

    return (
        <div className="portal-org-detail-main">
            <SectionCard
                title={t('admin.organizations.adminsTab.sectionTitle', { count: admins.length })}
                extra={
                    isSuperAdmin && (
                        <button
                            type="button"
                            className="portal-btn portal-btn-primary"
                            onClick={openAdminModal}
                        >
                            {t('admin.organizations.adminsTab.issueButton')}
                        </button>
                    )
                }
            >
                {adminsQuery.isLoading && (
                    <div role="status" aria-live="polite">
                        <span className="portal-sr-only">
                            {t('admin.organizations.loadingSrOnly')}
                        </span>
                        <SkeletonTable rows={2} columns={2} />
                    </div>
                )}
                {adminsQuery.isError && (
                    <ErrorState
                        description={t('admin.organizations.adminsTab.loadErrorDescription')}
                        onRetry={() => void adminsQuery.refetch()}
                        retrying={adminsQuery.isFetching}
                    />
                )}
                {adminsQuery.isSuccess && admins.length === 0 && (
                    <EmptyState
                        title={t('admin.organizations.adminsTab.emptyTitle')}
                        action={
                            isSuperAdmin && (
                                <button
                                    type="button"
                                    className="portal-btn portal-btn-primary"
                                    onClick={openAdminModal}
                                >
                                    {t('admin.organizations.adminsTab.emptyAction')}
                                </button>
                            )
                        }
                    />
                )}
                {admins.length > 0 && (
                    <div className="portal-table-scroll">
                        <table className="portal-admin-table">
                            <thead>
                                <tr>
                                    <th>{t('admin.organizations.adminsTab.columnEmail')}</th>
                                    <th>{t('admin.organizations.adminsTab.columnGrantedAt')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {admins.map((admin) => (
                                    <tr key={admin.id}>
                                        <td>{admin.user_email || admin.user_sub}</td>
                                        <td>
                                            {new Date(admin.granted_at).toLocaleString('vi-VN')}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {issued && (
                    <div className="portal-admin-secret" role="alert">
                        <strong>{t('admin.organizations.adminsTab.issuedBanner.title')}</strong>
                        <div>
                            {t('admin.organizations.adminsTab.issuedBanner.usernameLabel')}{' '}
                            <code>{issued.username}</code>
                        </div>
                        {issued.temp_password ? (
                            <div>
                                {t('admin.organizations.adminsTab.issuedBanner.tempPasswordLabel')}{' '}
                                <code>{issued.temp_password}</code>{' '}
                                {t('admin.organizations.adminsTab.issuedBanner.tempPasswordNote')}
                            </div>
                        ) : (
                            <div>
                                {t(
                                    'admin.organizations.adminsTab.issuedBanner.existingAccountNote'
                                )}
                            </div>
                        )}
                        <button type="button" onClick={() => setIssued(null)}>
                            {t('admin.organizations.adminsTab.issuedBanner.ackButton')}
                        </button>
                    </div>
                )}
            </SectionCard>

            {isSuperAdmin && (
                <Modal
                    open={adminModalOpen}
                    onCancel={closeAdminModal}
                    title={t('admin.organizations.adminsTab.modal.title')}
                    footer={
                        <>
                            <button
                                type="button"
                                className="portal-btn"
                                onClick={closeAdminModal}
                                disabled={issueAdminMutation.isPending}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="submit"
                                form="issue-admin-form"
                                className="portal-btn portal-btn-primary"
                                disabled={issueAdminMutation.isPending}
                            >
                                {issueAdminMutation.isPending
                                    ? t('admin.organizations.adminsTab.modal.submitPending')
                                    : t('admin.organizations.adminsTab.emptyAction')}
                            </button>
                        </>
                    }
                >
                    <form id="issue-admin-form" className="portal-admin-form" onSubmit={onIssueAdmin}>
                        <label>
                            {t('admin.organizations.adminsTab.columnEmail')}
                            <input
                                type="email"
                                value={adminForm.email}
                                onChange={(e) =>
                                    setAdminForm({ ...adminForm, email: e.target.value })
                                }
                                required
                            />
                        </label>
                        <label>
                            {t('admin.organizations.adminsTab.form.usernameLabel')}
                            <input
                                value={adminForm.username}
                                onChange={(e) =>
                                    setAdminForm({ ...adminForm, username: e.target.value })
                                }
                                required
                            />
                        </label>
                        {adminFormError && <p className="portal-admin-error">{adminFormError}</p>}
                    </form>
                </Modal>
            )}
        </div>
    );
}

export default OrgAdminsTab;
